import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, IconButton, Chip, MenuItem, Select, InputLabel, FormControl, FormHelperText, Alert, Checkbox, ListItemText, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { Add, Edit, Delete, LockReset } from '@mui/icons-material';
import api from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { exportToExcel, exportToPDF } from '../../utils/exportHelpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatPhoneNumber } from '../../utils/i18nHelpers';

interface Role {
  id: number;
  name: string;
}

interface User {
  id: number;
  name: string;
  name_ar?: string;
  email: string;
  phone?: string;
  roles?: Role[];
}

const Users: React.FC = () => {
  const { can } = useAuth();
  const { notifySuccess, notifyError } = useNotification();
  const { t, language } = useLanguage();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [form, setForm] = useState({ name: '', name_ar: '', email: '', password: '', phone: '', roles: [] as string[] });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [submitError, setSubmitError] = useState<string>('');
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordResetError, setPasswordResetError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      const all = Array.isArray(res.data) ? res.data : [];
      
      // Exclude service providers from Users list (they belong to Service Providers tab)
      const filtered = all.filter((u: any) => {
        const legacy = (u.role || '').toLowerCase() === 'provider' || (u.role || '').toLowerCase() === 'service_provider';
        const hasProviderRole = Array.isArray(u.roles) && u.roles.some((r: any) => {
          const rn = (r?.name || '').toLowerCase();
          return rn === 'provider' || rn === 'service_provider';
        });
        return !(legacy || hasProviderRole);
      });
      
      // Ensure roles are properly formatted - keep as objects with id and name
      const formattedUsers = filtered.map((u: any) => {
        let formattedRoles: any[] = [];
        if (Array.isArray(u.roles)) {
          formattedRoles = u.roles.map((r: any) => {
            if (typeof r === 'object' && r !== null) {
              return { id: r.id || 0, name: r.name || '' };
            } else if (typeof r === 'string') {
              return { id: 0, name: r };
            }
            return null;
          }).filter(Boolean);
        }
        
        return {
          ...u,
          roles: formattedRoles
        };
      });
      
      setUsers(formattedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchRoles = async () => {
    const res = await api.get('/roles');
    const all = Array.isArray(res.data) ? res.data : [];
    // Hide service_provider from Users roles picker
    setRoles(all.filter((r: any) => (r.name || '').toLowerCase() !== 'service_provider'));
  };

  useEffect(() => {
    if (can('users.view')) {
      fetchUsers();
      fetchRoles();
    }
  }, []);


  const openDialog = (user?: User) => {
    if (user) {
      setEditing(user);
      
      // Map roles correctly - extract role names from role objects
      let userRoles: string[] = [];
      
      // Check if user.roles exists and is an array
      if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
        userRoles = user.roles
          .map((r: any) => {
            // Handle different role formats
            if (typeof r === 'string') {
              return r.trim();
            } else if (r && typeof r === 'object') {
              // Handle role object with name property
              const roleName = r.name ? String(r.name).trim() : null;
              return roleName;
            }
            return null;
          })
          .filter((role: string | null): role is string => {
            return role !== null && role !== '' && role !== undefined;
          });
      }
      
      setForm({
        name: user.name || '',
        name_ar: user.name_ar || '',
        email: user.email || '',
        password: '',
        phone: user.phone || '',
        roles: userRoles,
      });
    } else {
      setEditing(null);
      setForm({ name: '', name_ar: '', email: '', password: '', phone: '', roles: [] });
    }
    setErrors({});
    setSubmitError('');
    setOpen(true);
  };

  const openPasswordReset = (user: User) => {
    setPasswordResetUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordResetError('');
    setPasswordResetOpen(true);
  };

  const closePasswordReset = () => {
    setPasswordResetOpen(false);
    setPasswordResetUser(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordResetError('');
  };

  const closeDialog = () => {
    setOpen(false);
    setErrors({});
    setSubmitError('');
  };

  const saveUser = async () => {
    try {
      setErrors({});
      setSubmitError('');
      
      // Strip service_provider if somehow present
      const cleanRoles = (form.roles || []).filter(r => r.toLowerCase() !== 'service_provider');
      
      // Build payload - password only for new users
      const payload: any = {
        name: form.name,
        name_ar: form.name_ar || undefined,
        phone: form.phone || undefined,
        roles: cleanRoles,
      };
      
      if (editing) {
        // For editing, don't send password
        await api.put(`/users/${editing.id}`, payload);
        notifySuccess(t('users.messages.save.successUpdate'));
      } else {
        // For new users, password is required
        if (!form.password || form.password.trim() === '') {
          setErrors({ password: [t('users.dialog.passwordRequired')] });
          return;
        }
        payload.password = form.password;
        await api.post('/users', payload);
        notifySuccess(t('users.messages.save.successCreate'));
      }
      
      await fetchUsers();
      closeDialog();
    } catch (error: any) {
      if (error?.response?.status === 422) {
        // Validation errors
        const validationErrors = error.response.data?.errors || {};
        setErrors(validationErrors);
        setSubmitError(t('users.messages.save.validationError'));
      } else {
        setSubmitError(
          error?.response?.data?.message ||
            error?.message ||
            t('users.messages.save.genericError')
        );
      }
      notifyError(t('users.messages.save.errorDefault'));
    }
  };

  const deleteUser = async (id: number) => {
    if (!can('users.delete')) return;
    if (window.confirm(t('users.messages.delete.confirm'))) {
      try {
        await api.delete(`/users/${id}`);
        fetchUsers();
        notifySuccess(t('users.messages.delete.success'));
      } catch (error: any) {
        notifyError(error?.response?.data?.message || t('users.messages.delete.errorDefault'));
      }
    }
  };

  const resetPassword = async () => {
    if (!passwordResetUser) return;
    
    try {
      setPasswordResetError('');
      
      // Validate passwords
      if (!newPassword || newPassword.trim() === '') {
        setPasswordResetError(t('users.messages.passwordReset.required'));
        return;
      }
      
      if (newPassword.length < 8) {
        setPasswordResetError(t('users.messages.passwordReset.minLength'));
        return;
      }
      
      if (newPassword !== confirmPassword) {
        setPasswordResetError(t('users.messages.passwordReset.mismatch'));
        return;
      }
      
      await api.put(`/users/${passwordResetUser.id}`, {
        password: newPassword,
      });
      
      closePasswordReset();
      notifySuccess(t('users.messages.passwordReset.success'));
    } catch (error: any) {
      if (error?.response?.status === 422) {
        const validationErrors = error.response.data?.errors || {};
        setPasswordResetError(
          validationErrors.password?.[0] || t('users.messages.passwordReset.validationError')
        );
      } else {
        setPasswordResetError(
          error?.response?.data?.message ||
            error?.message ||
            t('users.messages.passwordReset.genericError')
        );
      }
      notifyError(t('users.messages.passwordReset.genericError'));
    }
  };

  if (!can('users.view')) {
    return <Typography>{t('users.access.denied')}</Typography>;
  }

  // Helper function to check if user has technician role
  const hasTechnicianRole = (user: User): boolean => {
    if (!user.roles || !Array.isArray(user.roles)) return false;
    return user.roles.some((r: any) => {
      const roleName = typeof r === 'object' && r !== null ? r.name : r;
      return roleName?.toLowerCase() === 'technician';
    });
  };

  const filteredUsers = users.filter((u) => {
    const term = searchTerm.toLowerCase();
    const roleNames = (u.roles || []).map(r => r.name?.toLowerCase() || '').join(' ');
    return (
      u.name.toLowerCase().includes(term) ||
      (u.phone || '').toLowerCase().includes(term) ||
      roleNames.includes(term)
    );
  });

  const handleExportExcel = async () => {
    if (!filteredUsers.length) {
      notifyError(t('users.export.noData'));
      return;
    }
    const rows = filteredUsers.map((u) => ({
      [t('users.export.columns.name')]: u.name,
      [t('users.export.columns.phone')]: u.phone || '',
      [t('users.export.columns.roles')]: (u.roles || []).map(r => r.name).join(', '),
    }));
    await exportToExcel(rows, 'users');
    notifySuccess(t('users.export.excel.success'));
  };

  const handleExportPDF = async () => {
    if (!filteredUsers.length) {
      notifyError(t('users.export.noData'));
      return;
    }
    const columns = [
      t('users.export.columns.name'),
      t('users.export.columns.phone'),
      t('users.export.columns.roles')
    ];
    const data = filteredUsers.map((u) => [
      u.name,
      u.phone || '',
      (u.roles || []).map(r => r.name).join(', '),
    ]);
    await exportToPDF(columns, data, 'users', language, t('users.title'));
    notifySuccess(t('users.export.pdf.success'));
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={1} flexWrap="wrap">
        <Typography variant="h4">{t('users.title')}</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder={t('users.search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {can('users.export-excel') && (
            <Button variant="outlined" onClick={handleExportExcel}>
              {t('users.export.excel')}
            </Button>
          )}
          {can('users.export-pdf') && (
            <Button variant="outlined" onClick={handleExportPDF}>
              {t('users.export.pdf')}
            </Button>
          )}
          {can('users.add') && (
            <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()}>
              {t('users.actions.add')}
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>{t('users.table.name')}</strong></TableCell>
              <TableCell><strong>{t('users.table.nameAr')}</strong></TableCell>
              <TableCell><strong>{t('users.table.phone')}</strong></TableCell>
              <TableCell><strong>{t('users.table.roles')}</strong></TableCell>
              <TableCell align="right"><strong>{t('users.table.actions')}</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t('users.table.noData')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map(user => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.name}</TableCell>
                  <TableCell dir="rtl">{user.name_ar || '-'}</TableCell>
                  <TableCell>{user.phone ? formatPhoneNumber(user.phone, language) : '-'}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {(user.roles || []).map((r: any) => {
                        const roleName = typeof r === 'object' && r !== null ? r.name : r;
                        const roleId = typeof r === 'object' && r !== null ? r.id : 0;
                        return <Chip key={roleId || roleName} label={roleName} size="small" />;
                      })}
                      {(!user.roles || user.roles.length === 0) && (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {can('users.edit') && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => openDialog(user)}
                          title={t('users.actions.edit')}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openPasswordReset(user)}
                          title={t('users.actions.resetPassword')}
                        >
                          <LockReset />
                        </IconButton>
                      </>
                    )}
                    {can('users.delete') && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteUser(user.id)}
                        title={t('users.actions.delete')}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? t('users.dialog.editTitle') : t('users.dialog.addTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {submitError && (
              <Alert severity="error">{submitError}</Alert>
            )}
            <TextField
              label={t('users.dialog.fullName')}
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              required
              error={!!errors.name}
              helperText={errors.name?.[0]}
            />
            <TextField
              label={t('users.dialog.fullNameAr')}
              value={form.name_ar}
              onChange={e => setForm({ ...form, name_ar: e.target.value })}
              error={!!errors.name_ar}
              helperText={errors.name_ar?.[0]}
              dir="rtl"
            />
            {!editing && (
              <TextField 
                label={t('users.dialog.password')}
                type="password" 
                value={form.password} 
                onChange={e => setForm({ ...form, password: e.target.value })} 
                required
                error={!!errors.password}
                helperText={errors.password?.[0]}
              />
            )}
            <TextField 
              label={t('users.dialog.phone')}
              value={form.phone} 
              onChange={e => setForm({ ...form, phone: e.target.value })} 
              error={!!errors.phone}
              helperText={errors.phone?.[0]}
            />
            <FormControl fullWidth error={!!errors.roles}>
              <InputLabel id="roles-label" shrink={form.roles.length > 0}>
                {t('users.dialog.roles.label')}
              </InputLabel>
              <Select
                labelId="roles-label"
                multiple
                value={form.roles}
                label={t('users.dialog.roles.label')}
                onChange={(e) => {
                  const value = e.target.value;
                  const newRoles = typeof value === 'string' ? value.split(',') : value as string[];
                  setForm({ ...form, roles: newRoles });
                }}
                renderValue={(selected: unknown) => {
                  const selectedArray = Array.isArray(selected) ? selected : [];
                  if (selectedArray.length === 0) {
                    return <em>{t('users.dialog.roles.placeholder')}</em>;
                  }
                  return selectedArray.join(', ');
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
                key={`roles-select-${editing?.id || 'new'}-${form.roles.join(',')}`}
              >
                {roles.length > 0 ? (
                  roles.map(r => {
                    const isSelected = Array.isArray(form.roles) && form.roles.includes(r.name);
                    return (
                      <MenuItem 
                        key={r.id} 
                        value={r.name}
                      >
                        <Checkbox checked={isSelected} />
                        <ListItemText primary={r.name} />
                      </MenuItem>
                    );
                  })
                ) : (
                  <MenuItem disabled>No roles available</MenuItem>
                )}
              </Select>
              {errors.roles && <FormHelperText>{errors.roles[0]}</FormHelperText>}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t('users.dialog.cancel')}</Button>
          <Button variant="contained" onClick={saveUser}>
            {editing ? t('users.dialog.update') : t('users.dialog.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordResetOpen} onClose={closePasswordReset} maxWidth="sm" fullWidth>
        <DialogTitle>{t('users.passwordDialog.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {passwordResetError && (
              <Alert severity="error">{passwordResetError}</Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              {t('users.passwordDialog.description')
                .replace('{{name}}', passwordResetUser?.name || '')
                .replace('{{email}}', '')}
            </Typography>
            <TextField 
              label={t('users.passwordDialog.newPassword')}
              type="password" 
              value={newPassword} 
              onChange={e => setNewPassword(e.target.value)} 
              required
              fullWidth
              helperText={t('users.passwordDialog.helper')}
            />
            <TextField 
              label={t('users.passwordDialog.confirmPassword')}
              type="password" 
              value={confirmPassword} 
              onChange={e => setConfirmPassword(e.target.value)} 
              required
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePasswordReset}>{t('users.passwordDialog.cancel')}</Button>
          <Button variant="contained" onClick={resetPassword}>
            {t('users.passwordDialog.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;


